/* Sticky mobile add-to-cart
   Project: Veryplants CRO fixes (Apr 2026)
   Subscribes to the theme's pubsub for variant changes (constants.js: PUB_SUB_EVENTS).
*/

(function () {
  if (customElements.get('sticky-atc')) return;

  class StickyATC extends HTMLElement {
    constructor() {
      super();
      this.priceEl = this.querySelector('[data-price]');
      this.variantEl = this.querySelector('[data-variant-title]');
      this.addBtn = this.querySelector('[data-add-to-cart]');
      this.addLabel = this.querySelector('.sticky-atc__add-label');
      this.scrollBtn = this.querySelector('[data-scroll-to-top]');
      this.productHandle = this.dataset.productHandle;
      this.productUrl = this.dataset.productUrl;
      this.showAfter = Number(this.dataset.showAfter) || 250;
      this.currentVariantId = this.addBtn?.dataset.variantId
        ? Number(this.addBtn.dataset.variantId)
        : null;
      this.product = null;
      this.isVisible = false;
      this.unsubscribe = null;
    }

    connectedCallback() {
      // If another <sticky-atc> already exists earlier in the DOM, this duplicate
      // should silently disappear. Prevents "ADDADD" double-render when a section
      // update or app re-injects a second instance.
      const existing = document.querySelectorAll('sticky-atc');
      if (existing.length > 1 && existing[0] !== this) {
        try { this.remove(); } catch (e) {}
        return;
      }
      this.dedupeSlots();
      this.fetchProduct();
      this.bindEvents();
      this.observeMainATC();
      this.toggleVisible(false);
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    async fetchProduct() {
      try {
        const r = await fetch(`${this.productUrl}.js`);
        if (r.ok) this.product = await r.json();
      } catch (e) {
        // Bar still works using the server-rendered initial values.
      }
      // Sync once on init so the bar reflects the variant pre-selected via URL
      // (e.g. Klaviyo Notify-me links land with ?variant=<sold-out-id>).
      // Without this, the bar stays in the default ADD state for sold-out
      // variants and creates rage clicks when users tap a non-functional button.
      this.syncFromMainSelector();
    }

    bindEvents() {
      this.addBtn.addEventListener('click', (e) => this.handleAdd(e));
      this.scrollBtn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      // Subscribe to the theme's variant-change pub/sub if present (constants.js + pubsub.js).
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = window.subscribe(
          window.PUB_SUB_EVENTS.variantChange,
          (data) => {
            const v = data && (data.data?.variant || data.variant);
            if (v) this.updateVariant(v);
          }
        );
      }

      // Belt-and-braces: also poll the main form's variant input.
      const mainForm = document.querySelector('product-info form[action*="/cart/add"]');
      if (mainForm) {
        const idInput = mainForm.querySelector('input[name="id"]');
        if (idInput) {
          new MutationObserver(() => this.syncFromMainSelector())
            .observe(idInput, { attributes: true, attributeFilter: ['value'] });
        }
      }
    }

    syncFromMainSelector() {
      const idInput = document.querySelector('product-info form[action*="/cart/add"] input[name="id"]');
      if (!idInput || !this.product) return;
      const id = Number(idInput.value);
      if (!id) return;
      const variant = this.product.variants.find((v) => v.id === id);
      if (variant) this.updateVariant(variant);
    }

    updateVariant(variant) {
      this.currentVariantId = variant.id;
      this.addBtn.dataset.variantId = variant.id;
      // Defensive: re-resolve element refs in case DOM was re-rendered by a
      // section update (cart drawer refresh, app re-render). Stale refs would
      // mean updates land on detached nodes while a fresh duplicate sits visible.
      this.priceEl = this.querySelector('[data-price]');
      this.variantEl = this.querySelector('[data-variant-title]');
      this.addLabel = this.querySelector('.sticky-atc__add-label');
      // Clean any duplicate slots a 3rd-party script may have injected. Keep the first
      // of each slot, remove any siblings with the same selector. This is the fix for
      // the "ADDADD"/"$22.99$74.99"/concatenated-variant-titles bug seen in Propel
      // session 2602353356 on in-app browsers.
      this.dedupeSlots();
      if (this.priceEl) this.priceEl.replaceChildren(document.createTextNode(this.formatMoney(variant.price)));
      if (this.variantEl) {
        this.variantEl.replaceChildren(document.createTextNode(variant.public_title || variant.title || ''));
      }
      const available = variant.available;
      this.addBtn.disabled = !available;
      this.addBtn.setAttribute('aria-disabled', String(!available));
      if (this.addLabel) this.addLabel.replaceChildren(document.createTextNode(available ? 'ADD' : 'SOLD OUT'));
    }

    dedupeSlots() {
      // If multiple [data-price] / [data-variant-title] / .sticky-atc__add-label
      // exist (shouldn't, but defensive), remove all but the first.
      ['[data-price]', '[data-variant-title]', '.sticky-atc__add-label'].forEach((sel) => {
        const matches = this.querySelectorAll(sel);
        if (matches.length > 1) {
          for (let i = 1; i < matches.length; i++) matches[i].remove();
        }
      });
      // If multiple [data-add-to-cart] buttons inside sticky-atc, keep first
      const buttons = this.querySelectorAll('[data-add-to-cart]');
      if (buttons.length > 1) {
        for (let i = 1; i < buttons.length; i++) buttons[i].remove();
      }
    }

    async handleAdd(event) {
      event.preventDefault();

      if (!this.currentVariantId) {
        const sizePicker = document.querySelector(
          'variant-radios, fieldset[name="Size"], .product-form__input'
        );
        if (sizePicker) {
          sizePicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      this.setLoading(true);
      try {
        // Pull section IDs to refresh so the cart drawer updates.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:before-add', { bubbles: true, detail: { sectionsToBundle } })
        );
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((s) => sectionsToBundle.push(s.id));
        }

        const body = { items: [{ id: this.currentVariantId, quantity: 1 }] };
        if (sectionsToBundle.length) body.sections = sectionsToBundle.join(',');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('add-to-cart failed');
        const json = await res.json();

        // Push new sections into the cart drawer (Dawn pattern).
        if (cartDrawer && json.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(json);
        }

        // Theme pubsub event so other listeners refresh.
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-atc',
            productVariantId: this.currentVariantId,
            cartData: json,
          });
        }

        // Open the cart drawer if present.
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

        // Brief success state.
        if (this.addLabel) {
          const original = this.addLabel.textContent;
          this.addLabel.textContent = 'ADDED ✓';
          setTimeout(() => (this.addLabel.textContent = original), 1200);
        }
      } catch (err) {
        if (this.addLabel) {
          this.addLabel.textContent = 'TRY AGAIN';
          setTimeout(() => (this.addLabel.textContent = 'ADD'), 1500);
        }
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.addBtn.classList.toggle('is-loading', isLoading);
      this.addBtn.disabled = isLoading;
    }

    observeMainATC() {
      const mainAtc = document.querySelector(
        'product-info button[name="add"], product-form button[name="add"], button[name="add"][type="submit"]'
      );
      if (!mainAtc || typeof IntersectionObserver === 'undefined') {
        // Fallback: show after the user scrolls past the configured threshold.
        const onScroll = () => this.toggleVisible(window.scrollY > this.showAfter);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          this.toggleVisible(!entries[0].isIntersecting);
        },
        { threshold: 0.1 }
      );
      observer.observe(mainAtc);
    }

    toggleVisible(show) {
      if (show === this.isVisible) return;
      this.isVisible = show;
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-atc-visible', show);
    }

    formatMoney(cents) {
      if (window.Shopify && Shopify.formatMoney) {
        try {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        } catch (e) {}
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('sticky-atc', StickyATC);
})();
